import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Link,
  MenuDivider,
  MenuItem,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";

import SimpleSelect from "@/common/components/SimpleSelect";
import { useLinearTemplateActions } from "@/integration/data/useLinearTemplateActions";
import {
  LinearAgent,
  LinearIssueTemplate,
} from "@reflag/shared/linearConnectionAPI";

export function LinearCleanupTemplateSelect({
  value,
  onChange,
  templates,
  agents,
  workspaceUrlKey,
  isLoading = false,
  isError = false,
  isConnected = true,
  showUnavailableWarning = true,
  onRetry,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  templates: LinearIssueTemplate[];
  agents: LinearAgent[];
  workspaceUrlKey?: string;
  isLoading?: boolean;
  isError?: boolean;
  isConnected?: boolean;
  showUnavailableWarning?: boolean;
  onRetry?: () => void;
}) {
  const actions = useLinearTemplateActions();
  const [error, setError] = useState<string | null>(null);
  const busy =
    actions.create.isPending ||
    actions.inspect.isPending ||
    actions.setDelegate.isPending;
  const selectedTemplate = templates.find((template) => template.id === value);
  const optionsReady = isConnected && !isLoading && !isError;
  const unavailable =
    showUnavailableWarning &&
    optionsReady &&
    templates.length > 0 &&
    value &&
    !selectedTemplate;
  const editUrl =
    selectedTemplate && workspaceUrlKey
      ? `https://linear.app/${encodeURIComponent(workspaceUrlKey)}/settings/templates/issue/${encodeURIComponent(selectedTemplate.id)}/edit`
      : undefined;
  const linearAgent = agents.find((agent) =>
    [agent.name, agent.displayName].some((name) =>
      /^(linear|linear agent)$/i.test(name.trim()),
    ),
  );

  async function selectTemplate(id: string) {
    setError(null);
    try {
      // Refresh template metadata, including the delegatee, before selection.
      const template = await actions.inspect.mutateAsync(id);
      onChange(template.id);
    } catch {
      setError("Could not check the Linear template. Try selecting it again.");
    }
  }

  async function createTemplate() {
    setError(null);
    try {
      const result = await actions.create.mutateAsync();
      onChange(result.template.id);
    } catch {
      setError(
        "Could not create the template. Check Linear before trying again.",
      );
    }
  }

  async function setLinearDelegate() {
    if (!selectedTemplate || !linearAgent) return;
    setError(null);
    try {
      await actions.setDelegate.mutateAsync({
        id: selectedTemplate.id,
        delegateId: linearAgent.id,
      });
    } catch {
      setError("Could not set the template delegatee. Try again.");
    }
  }

  return (
    <>
      <FormControl>
        <FormLabel>Workspace issue template</FormLabel>
        {!isConnected ? (
          <Text color="dimmed" fontSize="sm">
            Connect or reauthorize Linear to load templates.
          </Text>
        ) : isLoading ? (
          <Text color="dimmed" fontSize="sm" role="status">
            Loading templates…
          </Text>
        ) : isError ? (
          <Alert status="error">
            <AlertIcon />
            <Text fontSize="sm">Could not load Linear templates.</Text>
            {onRetry && (
              <Button ml={2} size="sm" variant="link" onClick={onRetry}>
                Try again
              </Button>
            )}
          </Alert>
        ) : null}
        <HStack>
          <SimpleSelect
            _menuList={{
              minW: "min(24rem, calc(100vw - 2rem))",
              maxW: "calc(100vw - 2rem)",
            }}
            w="50%"
            minW={0}
            value={selectedTemplate?.id ?? ""}
            onChange={(id) => void selectTemplate(id)}
            isDisabled={busy || !optionsReady}
            isLoading={busy || isLoading}
            placeholder="Select a template"
            options={templates.map((template) => ({
              label: template.name,
              value: template.id,
            }))}
            menuFooter={
              optionsReady && (
                <>
                  {templates.length > 0 ? (
                    <MenuDivider />
                  ) : (
                    <MenuItem isDisabled>No workspace templates found</MenuItem>
                  )}
                  <MenuItem
                    onClick={() => void createTemplate()}
                    isDisabled={busy}
                  >
                    Create default cleanup template
                  </MenuItem>
                </>
              )
            }
          />
          {editUrl && selectedTemplate && (
            <Link
              href={editUrl}
              isExternal
              fontSize="sm"
              flexShrink={0}
              aria-label={`Edit ${selectedTemplate.name} in Linear`}
            >
              Edit in Linear
            </Link>
          )}
        </HStack>
        <Text mt={2} color="dimmed" fontSize="sm">
          Reflag appends the flag key and name to the bottom of the template
          when creating each issue.
        </Text>
        {unavailable && (
          <Text mt={2} color="orange.600" fontSize="sm" role="status">
            Configured template is unavailable. Choose another template.
          </Text>
        )}
      </FormControl>
      {optionsReady && selectedTemplate && !selectedTemplate.delegateId && (
        <Alert status="warning" role="status" alignItems="flex-start">
          <AlertIcon />
          <Text fontSize="sm">
            The template selected does not delegate to an agent
            {linearAgent ? (
              <>
                ,{" "}
                <Button
                  variant="link"
                  fontSize="inherit"
                  textDecoration="underline"
                  whiteSpace="normal"
                  textAlign="left"
                  isDisabled={busy}
                  isLoading={actions.setDelegate.isPending}
                  onClick={() => void setLinearDelegate()}
                >
                  update it to delegate to Linear agent
                </Button>
                .
              </>
            ) : editUrl ? (
              <>
                ,{" "}
                <Link href={editUrl} isExternal textDecoration="underline">
                  update it in Linear
                </Link>
                .
              </>
            ) : (
              "."
            )}
          </Text>
        </Alert>
      )}
      {error && (
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      )}
    </>
  );
}
